import { t } from "@lingui/macro";
import { useLinkProps } from "@react-navigation/native";
import graphql from "babel-plugin-relay/macro";
import { format, parseISO } from "date-fns";
import { useFragment } from "react-relay";

import { InfoBannerCautionMinorIcon } from "@whoosh/design-system/src/components/display/InfoBanner";
import {
  BlockIcon,
  CheckIcon,
  ChevronRightIcon,
} from "@whoosh/design-system/src/components/icons_v2/general";
import {
  Box,
  HStack,
  Pressable,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";

import {
  formatStatusTime,
  formatTime,
  formatTimeRange,
} from "../../formatters";
import { SavedRequestRow_booking$key } from "./__generated__/SavedRequestRow_booking.graphql";

type Props = {
  bookingRef: SavedRequestRow_booking$key;
  onClose: () => void;
};

export const SavedRequestRow = ({ bookingRef, onClose }: Props) => {
  const booking = useFragment(fragment, bookingRef);
  const linkProps = useLinkProps({
    screen: "BookingSummary",
    params: { slotOrBookingId: booking.id },
  });
  const date = booking.startSlot?.agenda?.date;
  const dateLabel = date ? format(parseISO(date), "EEE, MMM do") : "";
  const isRequest = booking.startSlot?.__typename === "QueueSlot";
  const request =
    booking.__typename === "StandardBooking" && isRequest
      ? booking.request
      : null;
  const time = formatTime(isRequest ? request?.time : booking.startSlot?.time);
  const window =
    request && request.minTime !== request.maxTime
      ? formatTimeRange(request.minTime, request.maxTime)
      : null;
  const statuses = booking.requestRestrictionStatuses;
  const isBlocked = statuses.some((status) => status.blockingScope === "FULL");
  const hasWarning = statuses.length > 0;

  return (
    <Pressable
      {...linkProps}
      onPress={(event) => {
        onClose();
        linkProps.onPress(event);
      }}
      accessibilityLabel={t`Open request for ${dateLabel}`}
      testID={`recurring-request-saved-booking-${booking.id}`}
    >
      <HStack
        padding={4}
        gap={3}
        alignItems="center"
        borderRadius="4"
        borderWidth={1}
        borderColor="surface-dividers"
        backgroundColor="surface-primary"
      >
        <Box
          width={26}
          height={26}
          borderRadius="8"
          alignItems="center"
          justifyContent="center"
          backgroundColor={
            isBlocked
              ? "surface-status-error-subtler"
              : hasWarning
              ? "surface-status-warning-subtler"
              : "surface-status-success-subtler"
          }
        >
          {isBlocked ? (
            <BlockIcon
              width={16}
              height={16}
              color="icon-on-color-error"
              testID={`recurring-request-saved-blocked-${booking.id}`}
            />
          ) : hasWarning ? (
            <InfoBannerCautionMinorIcon
              width={14}
              height={14}
              color="icon-on-color-warning"
              testID={`recurring-request-saved-warning-${booking.id}`}
            />
          ) : (
            <CheckIcon
              width={20}
              height={20}
              color="icon-on-color-success"
              testID={`recurring-request-saved-check-${booking.id}`}
            />
          )}
        </Box>
        <VStack gap={2} flex={1}>
          <HStack justifyContent="space-between" flexWrap="wrap" gap={1}>
            <Text variant="mainBaseSemiBold">{dateLabel}</Text>
            <Text variant="mainBaseMedium" color="text-secondary">
              {time}
            </Text>
          </HStack>
          {window ? (
            <Text
              variant="mainSmall"
              color="text-secondary"
            >{t`Requested window: ${window}`}</Text>
          ) : null}
          {statuses.map((status) => (
            <VStack
              key={`${status.type}-${status.label}-${status.startAt}-${status.endAt}-${status.blockingScope}`}
              padding={2}
              gap={1}
              borderRadius="2"
              backgroundColor={
                status.blockingScope === "FULL"
                  ? "surface-status-error-subtlest"
                  : "surface-status-warning-subtlest"
              }
            >
              <Text variant="mainSmallMedium" color="text-secondary">
                {status.label}
              </Text>
              <Text variant="mainSmall" color="text-secondary">
                {formatStatusTime(status.startAt, status.endAt)}
              </Text>
            </VStack>
          ))}
        </VStack>
        <ChevronRightIcon width={16} height={16} color="icon-main-secondary" />
      </HStack>
    </Pressable>
  );
};

const fragment = graphql`
  fragment SavedRequestRow_booking on Booking {
    __typename
    id
    startSlot {
      __typename
      ... on TimeSlot {
        time
      }
      ... on BucketTimeSlot {
        time
      }
      agenda {
        date
      }
    }
    ... on StandardBooking {
      request {
        time
        minTime
        maxTime
      }
    }
    requestRestrictionStatuses {
      type
      blockingScope
      label
      startAt
      endAt
    }
  }
`;
